import { styled } from '@mui/material/styles';
import { ButtonBase } from '@mui/material';

// The two app-bar popover triggers and their menus, after the base brand of
// react-cabinet: a language pill (flag, code, caret) and a round account button,
// both opening the same compact panel. Its literals are mapped onto this theme's
// palette, so both modes work: the account fill is primary.tint6 (the base
// brand's #F1F6FE), the grey alphas are action.hover / action.selected,
// divider and border.input.

const FLAG_WIDTH = 22;
const FLAG_HEIGHT = 16;
// Trigger height and account avatar size. A little under the bar's 40px icon
// buttons, as in the base brand: they read as controls, not as icons.
const TRIGGER_SIZE = 36;
const ACCOUNT_GLYPH = 19;
// Inner padding of the panel; rows and dividers are inset against it.
const PANEL_PADDING = 0.625;

// `open` keeps the fill while the menu is up.
export const LanguageTrigger = styled(ButtonBase, {
    shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
    'height': TRIGGER_SIZE,
    'display': 'flex',
    'alignItems': 'center',
    'gap': 7,
    'padding': theme.spacing(0, 1, 0, 0.875),
    'borderRadius': theme.shape.option,
    'fontSize': theme.typography.pxToRem(13),
    'fontWeight': theme.typography.fontWeightSemiBold ?? 600,
    'lineHeight': 1,
    'color': open ? theme.palette.text.primary : theme.palette.text.secondary,
    'backgroundColor': open ? theme.palette.action.selected : 'transparent',
    'transition': theme.transitions.create(['background-color', 'color'], {
        duration: theme.transitions.duration.shortest
    }),
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary
    }
}));

// country-flag-icons draws in its own 3:2 viewBox, so the flag is sized
// through this box; the hairline ring keeps a white flag off a white bar.
export const FlagBox = styled('span')(({ theme }) => ({
    'display': 'block',
    'width': FLAG_WIDTH,
    'height': FLAG_HEIGHT,
    'flex': `0 0 ${FLAG_WIDTH}px`,
    'borderRadius': 3,
    'overflow': 'hidden',
    'boxShadow': `0 0 0 1px ${theme.palette.border.input}`,
    '& svg': {
        width: '100%',
        height: '100%',
        display: 'block'
    }
}));

// A phone keeps the flag alone: the bar there also carries the page title.
export const languageMetaSx = {
    display: { xs: 'none', sm: 'block' }
};

export const languageCaretSx = {
    display: { xs: 'none', sm: 'block' },
    flex: '0 0 14px',
    width: 14,
    height: 14,
    // A function, not the palette path as a string, which the translation
    // check would take for a key.
    color: (theme) => theme.palette.text.placeholder
};

// The ring is a box-shadow, not a border, so it does not eat into the avatar;
// it turns primary while hovered and while the menu is open.
export const accountTriggerSx = (open) => (theme) => {
    const active = `0 0 0 2px ${theme.palette.focus.halo}`;
    return {
        'padding': 0,
        'borderRadius': '50%',
        'boxShadow': open ? active : `0 0 0 1px ${theme.palette.divider}`,
        'transition': theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.shortest
        }),
        '&:hover': { boxShadow: active }
    };
};

export const accountAvatarSx = {
    'width': TRIGGER_SIZE,
    'height': TRIGGER_SIZE,
    'bgcolor': 'primary.tint6',
    'color': 'primary.softText',
    '& .MuiSvgIcon-root': {
        width: ACCOUNT_GLYPH,
        height: ACCOUNT_GLYPH
    }
};

// Both panels: radius, surface and dropdown shadow come from the MuiPopover
// override; here the width, a hairline edge, the inner padding and the rows.
export const menuPaperSx = (width) => (theme) => ({
    'marginTop': theme.spacing(1),
    'padding': theme.spacing(PANEL_PADDING),
    width,
    'border': `1px solid ${theme.palette.divider}`,
    'backgroundImage': 'none',
    '& .MuiMenuItem-root': {
        minHeight: 34,
        padding: theme.spacing(0, 1.125),
        borderRadius: theme.shape.borderRadius,
        fontSize: theme.typography.pxToRem(13),
        color: theme.palette.text.secondary,
        '&:hover': { color: theme.palette.text.primary },
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary.tint12,
            color: theme.palette.text.primary
        }
    }
});

// The panel is padded, so a divider reaches back out to its edges to read as
// a section rule rather than a floating line.
export const menuDividerSx = {
    mx: -PANEL_PADDING,
    my: 0.5
};

// The name keeps the rows' 9px inset itself, so the email row under it can
// span the panel as a menu row does.
export const accountIdentitySx = {
    minWidth: 0,
    pt: 0.5,
    pb: 1
};

export const accountNameSx = (theme) => ({
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: '17px',
    color: theme.palette.text.primary,
    padding: theme.spacing(0, 1.125),
    marginBottom: theme.spacing(0.75)
});

// Email row: the rows' 34px height and 9px inset, filled and borderless.
export const emailRowSx = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    height: 34,
    paddingLeft: theme.spacing(1.125),
    paddingRight: theme.spacing(0.5),
    borderRadius: `${theme.shape.borderRadius}px`,
    backgroundColor: theme.palette.background.neutral
});

// `userSelect: all`, so the address can still be taken by hand where the
// clipboard is not available.
export const emailValueSx = (theme) => ({
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    userSelect: 'all',
    fontSize: theme.typography.pxToRem(12.5),
    lineHeight: '16px',
    color: theme.palette.text.secondary
});

export const emailCopySx = (copied) => (theme) => {
    const color = copied ? theme.palette.text.success : theme.palette.text.secondary;
    return {
        'flex': '0 0 26px',
        'width': 26,
        'height': 26,
        'fontSize': theme.typography.pxToRem(15),
        color,
        '&:hover': { color: copied ? color : theme.palette.text.primary }
    };
};
